<template>
  
  <PageHeader
    title="LLM Suggestion Type Configuration"
    subtitle="Configure LLM suggestion types, their schemas, and display settings"
    :breadcrumbs="[{ label: 'Admin Console' }]"
  >
    <template #actions>
      <div class="ml-auto flex gap-2">
        <button
          @click="showGenerateModal = true"
          class="btn-secondary"
        >
          <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
          Generate with AI
        </button>
        <button
          @click="openCreateModal"
          class="btn-primary"
        >
          <font-awesome-icon :icon="['fas', 'plus']" />
          Create New Suggestion Type
        </button>
      </div>
    </template>
  </PageHeader>

  <div class="space-y-6 p-4">

    <!-- Suggestion Types List -->
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              ID
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Schema Status
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Display Schema
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="suggestionType in suggestionTypes" :key="suggestionType.id">
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ suggestionType.id }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {{ suggestionType.name }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              <span v-if="hasSchema(suggestionType)" class="text-green-600 flex items-center gap-1">
                <FontAwesomeIcon :icon="['fas', 'check-circle']" class="text-xs" />
                Has Schema
              </span>
              <span v-else class="text-gray-400">
                No Schema
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              <span v-if="hasDisplaySchema(suggestionType)" class="text-blue-600 flex items-center gap-1">
                <FontAwesomeIcon :icon="['fas', 'palette']" class="text-xs" />
                Has Display
              </span>
              <span v-else class="text-gray-400">
                No Display
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ formatDate(suggestionType.created_at) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                :class="suggestionType.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
              >
                {{ suggestionType.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <button
                @click="editSuggestionType(suggestionType)"
                class="text-primary-600 hover:text-primary-900 mr-3"
              >
                Edit
              </button>
              <button
                @click="toggleActive(suggestionType)"
                :class="suggestionType.is_active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'"
              >
                {{ suggestionType.is_active ? 'Deactivate' : 'Activate' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create/Edit Suggestion Type Modal -->
    <div
      v-if="showCreateModal || showEditModal"
      class="fixed inset-0 bg-gray-500/75 overflow-y-auto h-full w-full z-50"
      @click.self="closeModals"
    >
      <div class="relative top-10 mx-auto p-5 w-full max-w-6xl">
        <div class="bg-white rounded-lg shadow-xl max-h-[85vh] overflow-hidden flex flex-col">
          <!-- Modal Header -->
          <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900">
              {{ showEditModal ? 'Edit' : 'Create New' }} Suggestion Type
            </h3>
            <button
              @click="closeModals"
              class="text-gray-400 hover:text-gray-500"
            >
              <FontAwesomeIcon :icon="['fas', 'times']" class="h-6 w-6" />
            </button>
          </div>

          <!-- Modal Body with Tabs -->
          <div class="flex-1 overflow-hidden flex flex-col">
            <!-- Tab Navigation -->
            <div class="px-6 pt-3">
              <Page_Tabs
                :tabs="editTabs"
                :initial-tab="editActiveTab"
                @select="editActiveTab = $event"
              />
            </div>

            <!-- Tab Content -->
            <div class="flex-1 overflow-y-auto p-6">
              <!-- Basic Info Tab -->
              <div v-if="editActiveTab === 'Basic Info'" class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Name <span class="text-red-500">*</span></label>
                  <input
                    v-model="formData.name"
                    placeholder="e.g., Entity Assessment, Meeting Structure"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <textarea
                    v-model="formData.description"
                    placeholder="Brief description of this suggestion type"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                    rows="3"
                  />
                </div>
              </div>

              <!-- Prompt Tab -->
              <div v-if="editActiveTab === 'Prompt'" class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Assessment Prompt</label>
                  <textarea
                    v-model="formData.prompt"
                    placeholder="Enter the prompt that will be used to generate assessments..."
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark font-mono text-sm"
                    rows="12"
                  />
                  <p class="text-xs text-gray-500 mt-1">
                    This prompt will be used by the AI to generate assessments. Be specific about what you want the assessment to include.
                  </p>
                </div>
              </div>

              <!-- Schemas Tab -->
              <div v-if="editActiveTab === 'Schemas'" class="space-y-6">
                <!-- JSON Schema -->
                <div>
                  <div class="flex justify-between items-center mb-2">
                    <label class="block text-sm font-medium text-gray-700">JSON Schema</label>
                    <div class="flex gap-2">
                      <button
                        @click="validateEditSchema('json')"
                        class="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                      >
                        <FontAwesomeIcon :icon="['fas', 'check']" class="mr-1" />
                        Validate
                      </button>
                      <button
                        @click="formatEditSchema('json')"
                        class="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                      >
                        <FontAwesomeIcon :icon="['fas', 'align-left']" class="mr-1" />
                        Format
                      </button>
                    </div>
                  </div>
                  <textarea
                    v-model="formData.json_schema"
                    @input="onEditSchemaChange('json')"
                    class="w-full h-64 p-4 font-mono text-sm border rounded-md focus:ring-2 focus:ring-blue-dark focus:border-blue-500"
                    :class="editSchemaErrors.json ? 'border-red-500' : 'border-gray-300'"
                    placeholder='{\n  "type": "object",\n  "properties": {},\n  "required": []\n}'
                  ></textarea>
                  <div v-if="editSchemaErrors.json" class="text-red-600 text-sm mt-1 flex items-center gap-1">
                    <FontAwesomeIcon :icon="['fas', 'exclamation-circle']" />
                    {{ editSchemaErrors.json }}
                  </div>
                </div>

                <!-- Display Schema -->
                <div>
                  <div class="flex justify-between items-center mb-2">
                    <label class="block text-sm font-medium text-gray-700">Display Schema</label>
                    <div class="flex gap-2">
                      <button
                        @click="validateEditSchema('display')"
                        class="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                      >
                        <FontAwesomeIcon :icon="['fas', 'check']" class="mr-1" />
                        Validate
                      </button>
                      <button
                        @click="formatEditSchema('display')"
                        class="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                      >
                        <FontAwesomeIcon :icon="['fas', 'align-left']" class="mr-1" />
                        Format
                      </button>
                    </div>
                  </div>
                  <textarea
                    v-model="formData.display_schema"
                    @input="onEditSchemaChange('display')"
                    class="w-full h-64 p-4 font-mono text-sm border rounded-md focus:ring-2 focus:ring-blue-dark focus:border-blue-500"
                    :class="editSchemaErrors.display ? 'border-red-500' : 'border-gray-300'"
                    placeholder='{\n  "elements": []\n}'
                  ></textarea>
                  <div v-if="editSchemaErrors.display" class="text-red-600 text-sm mt-1 flex items-center gap-1">
                    <FontAwesomeIcon :icon="['fas', 'exclamation-circle']" />
                    {{ editSchemaErrors.display }}
                  </div>
                </div>
              </div>

              <!-- Configuration Tab -->
              <div v-if="editActiveTab === 'Configuration'" class="space-y-6">
                <!-- Data Sources -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Default Data Sources
                  </label>
                  <p class="text-xs text-gray-500 mb-2">
                    Select which data sources should be used by default for assessments with this type
                  </p>
                  <div class="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3">
                    <div
                      v-for="source in availableDataSources"
                      :key="source.key"
                      class="flex items-start"
                    >
                      <input
                        :id="`edit-data-source-${source.key}`"
                        v-model="formData.default_data_sources"
                        type="checkbox"
                        :value="source.key"
                        class="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-dark border-gray-300 rounded"
                      />
                      <label
                        :for="`edit-data-source-${source.key}`"
                        class="ml-2 cursor-pointer"
                      >
                        <div class="text-sm font-medium text-gray-700">{{ source.label }}</div>
                        <div class="text-xs text-gray-500">{{ source.description }}</div>
                      </label>
                    </div>
                  </div>
                  <div class="mt-2 flex gap-2">
                    <button
                      @click="selectAllEditDataSources"
                      class="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Select All
                    </button>
                    <span class="text-gray-300">|</span>
                    <button
                      @click="deselectAllEditDataSources"
                      class="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                <!-- Web Search Toggle -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Web Search
                  </label>
                  <div class="flex items-center">
                    <input
                      id="enable-web-search"
                      v-model="formData.enable_web_search"
                      type="checkbox"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-dark border-gray-300 rounded"
                    />
                    <label for="enable-web-search" class="ml-2 text-sm text-gray-700">
                      Enable web search capability for assessments
                    </label>
                  </div>
                  <p class="text-xs text-gray-500 mt-1">
                    When enabled, the AI can search the web for additional context when generating assessments
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              @click="closeModals"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              @click="saveSuggestionType"
              :disabled="!formData.name.trim()"
              class="btn-primary-sm"
            >
              <FontAwesomeIcon :icon="['fas', 'save']" />
              {{ showEditModal ? 'Update' : 'Create' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Generate Schema Modal -->
    <div
      v-if="showGenerateModal"
      class="fixed inset-0 bg-gray-500/75 overflow-y-auto h-full w-full z-50"
      @click.self="closeGenerateModal"
    >
      <div class="relative top-10 mx-auto p-5 w-full max-w-4xl">
        <div class="bg-white rounded-lg shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
          <!-- Modal Header -->
          <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900">
              Generate Assessment Schema with AI
            </h3>
            <button
              @click="closeGenerateModal"
              class="text-gray-400 hover:text-gray-500"
            >
              <FontAwesomeIcon :icon="['fas', 'times']" class="h-6 w-6" />
            </button>
          </div>

          <!-- Modal Body -->
          <div class="flex-1 overflow-y-auto p-6">
            <div class="space-y-6">
              <!-- Assessment Description -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Assessment Description <span class="text-red-500">*</span>
                </label>
                <textarea
                  v-model="generateFormData.assessment_description"
                  placeholder="Describe what this assessment should evaluate. Be specific about the data points, metrics, and insights needed."
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows="4"
                />
              </div>

              <!-- Requirements -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Requirements (Optional)
                </label>
                <div class="flex gap-2 mb-2">
                  <input
                    v-model="generateFormData.requirementInput"
                    @keyup.enter="addRequirement"
                    placeholder="Add a requirement and press Enter"
                    class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <button
                    @click="addRequirement"
                    class="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    Add
                  </button>
                </div>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="(req, index) in generateFormData.requirements"
                    :key="index"
                    class="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-1"
                  >
                    {{ req }}
                    <button
                      @click="generateFormData.requirements.splice(index, 1)"
                      class="hover:text-purple-900"
                    >
                      <FontAwesomeIcon :icon="['fas', 'times']" class="text-xs" />
                    </button>
                  </span>
                </div>
              </div>

              <!-- Entity ID (Optional) -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Entity ID (Optional)
                </label>
                <input
                  v-model="generateFormData.entity_id"
                  type="number"
                  placeholder="Enter entity ID to use actual data for schema generation"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p class="text-xs text-gray-500 mt-1">
                  If provided, the AI will analyze actual entity data to create more relevant schemas
                </p>
              </div>

              <!-- Data Sources Selection -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Data Sources <span class="text-red-500">*</span>
                </label>
                <p class="text-xs text-gray-500 mb-2">
                  Select which data sources to include in the assessment context
                </p>
                <div class="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
                  <div
                    v-for="source in availableDataSources"
                    :key="source.key"
                    class="flex items-start"
                  >
                    <input
                      :id="`data-source-${source.key}`"
                      v-model="generateFormData.data_sources"
                      type="checkbox"
                      :value="source.key"
                      class="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <label
                      :for="`data-source-${source.key}`"
                      class="ml-2 cursor-pointer"
                    >
                      <div class="text-sm font-medium text-gray-700">{{ source.label }}</div>
                      <div class="text-xs text-gray-500">{{ source.description }}</div>
                    </label>
                  </div>
                </div>
                <div class="mt-2 flex gap-2">
                  <button
                    @click="selectAllDataSources"
                    class="text-xs text-purple-600 hover:text-purple-700"
                  >
                    Select All
                  </button>
                  <span class="text-gray-300">|</span>
                  <button
                    @click="deselectAllDataSources"
                    class="text-xs text-purple-600 hover:text-purple-700"
                  >
                    Deselect All
                  </button>
                  <span class="text-gray-300">|</span>
                  <button
                    @click="selectDefaultDataSources"
                    class="text-xs text-purple-600 hover:text-purple-700"
                  >
                    Default Selection
                  </button>
                </div>
              </div>

              <!-- Generated Result Preview -->
              <div v-if="generatedSchemas" class="border-t pt-6">
                <h4 class="text-sm font-medium text-gray-700 mb-3">Generated Result</h4>

                <div class="space-y-4">
                  <div class="bg-green-50 rounded-lg p-4">
                    <div class="text-sm font-medium text-green-800 mb-1">
                      {{ generatedSchemas.assessment_name }}
                    </div>
                    <div class="text-xs text-green-600">
                      {{ generatedSchemas.assessment_description }}
                    </div>
                  </div>

                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <div class="text-xs font-medium text-gray-600 mb-1">JSON Schema</div>
                      <pre class="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-40">{{ JSON.stringify(generatedSchemas.assessment_json_schema, null, 2) }}</pre>
                    </div>
                    <div>
                      <div class="text-xs font-medium text-gray-600 mb-1">Display Schema</div>
                      <pre class="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-40">{{ JSON.stringify(generatedSchemas.display_schema, null, 2) }}</pre>
                    </div>
                  </div>

                  <div v-if="generatedSchemas.example_data">
                    <div class="text-xs font-medium text-gray-600 mb-1">Example Data</div>
                    <pre class="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-40">{{ JSON.stringify(generatedSchemas.example_data, null, 2) }}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <div class="text-sm text-gray-600">
              {{ isGenerating ? 'Generating schemas...' : generatedSchemas ? 'Schemas generated successfully' : '' }}
            </div>
            <div class="flex gap-3">
              <button
                v-if="!generatedSchemas"
                @click="generateSchemas"
                :disabled="!generateFormData.assessment_description.trim() || isGenerating"
                class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <FontAwesomeIcon :icon="isGenerating ? ['fas', 'spinner'] : ['fas', 'magic']"
                                :spin="isGenerating" />
                {{ isGenerating ? 'Generating...' : 'Generate' }}
              </button>

              <button
                v-if="generatedSchemas"
                @click="useGeneratedSchemas"
                class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 flex items-center gap-2"
              >
                <FontAwesomeIcon :icon="['fas', 'check']" />
                Use These Schemas
              </button>

              <button
                v-if="generatedSchemas"
                @click="resetGeneration"
                class="px-4 py-2 border border-orange-300 rounded-md text-sm font-medium text-orange-700 bg-white hover:bg-orange-50"
              >
                Try Again
              </button>

              <button
                @click="closeGenerateModal"
                class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {{ generatedSchemas ? 'Close' : 'Cancel' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- JSON Visual Editor -->
    <JsonEditor
      v-if="showJsonEditor"
      :initial-data="parsedSchema || {}"
      @close="showJsonEditor = false"
      @save="saveFromJsonEditor"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faTimes, faPlus, faSave, faEdit, faTrash, faCheckCircle, faExclamationCircle,
  faCheck, faAlignLeft, faToggleOn, faToggleOff, faMagic, faSpinner, faPalette
} from '@fortawesome/free-solid-svg-icons';
import JsonEditor from '@/components/widgetcomponents/JSON_Editors/JSON_Editor.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';

library.add(
  faTimes, faPlus, faSave, faEdit, faTrash, faCheckCircle, faExclamationCircle,
  faCheck, faAlignLeft, faToggleOn, faToggleOff, faMagic, faSpinner, faPalette
);

export default {
  name: 'LLMSuggestionTypeAdmin',
  components: {
    FontAwesomeIcon,
    JsonEditor,
    PageHeader,
    Page_Tabs
  },
  data() {
    return {
      suggestionTypes: [],
      showCreateModal: false,
      showEditModal: false,
      showJsonEditor: false,
      showGenerateModal: false,
      editingSuggestionType: null,
      editActiveTab: 'Basic Info',
      editTabs: ['Basic Info', 'Prompt', 'Schemas', 'Configuration'],
      editSchemaErrors: {
        json: '',
        display: ''
      },
      formData: {
        name: '',
        description: '',
        prompt: '',
        json_schema: '',
        display_schema: '',
        default_data_sources: [],
        enable_web_search: false
      },
      generateFormData: {
        assessment_description: '',
        requirements: [],
        requirementInput: '',
        examples: [],
        exampleInput: '',
        entity_id: null,
        data_sources: ['entity_metadata', 'engagement_history', 'survey_responses']
      },
      availableDataSources: [
        { key: 'entity_metadata', label: 'Entity Metadata', description: 'Basic entity information' },
        { key: 'engagement_history', label: 'Engagement History', description: 'Recent engagements with the entity' },
        { key: 'survey_responses', label: 'Survey Responses', description: 'Survey responses from the entity' },
        { key: 'entity_tags', label: 'Entity Tags', description: 'InfoTag classifications for the entity' },
        { key: 'provider_summary', label: 'Provider Summary', description: 'Provider roster and specialty distribution' },
        { key: 'vbc_summary', label: 'VBC Summary', description: 'Value-based care experience and ACO participation' },
        { key: 'awv_summary', label: 'AWV Summary', description: 'Annual Wellness Visit performance metrics' },
        { key: 'cost_risk_summary', label: 'Cost/Risk Summary', description: 'Cost and risk-adjusted performance data' },
        { key: 'attribution_summary', label: 'Attribution Summary', description: 'Patient attribution counts and contract breakdown' },
        { key: 'utilization_summary', label: 'Utilization Summary', description: 'Utilization metrics (requires claims data)' }
      ],
      isGenerating: false,
      generatedSchemas: null,
      schemaTemplates: {
        entity_assessment: {
          type: "object",
          properties: {
            executive_summary: {
              type: "object",
              properties: {
                vbc_readiness_score: { type: "number", minimum: 0, maximum: 100 },
                primary_failure_mode: { type: "string" },
                estimated_time_to_proficiency: { type: "string" }
              },
              required: ["vbc_readiness_score", "primary_failure_mode", "estimated_time_to_proficiency"]
            },
            operational_architecture: { type: "object" },
            human_capital_analysis: { type: "object" },
            performance_forensics: { type: "object" },
            financial_reality: { type: "object" },
            critical_blockers: { type: "array" },
            actionable_recommendations: { type: "object" },
            reality_check: { type: "object" }
          },
          required: ["executive_summary"]
        },
        entity_summary: {
          type: "object",
          properties: {
            engagement_summary: { type: "string" },
            next_steps: {
              type: "array",
              items: { type: "string" }
            }
          },
          required: ["engagement_summary", "next_steps"]
        },
        meeting_structure: {
          type: "object",
          properties: {
            meeting_structure: { type: "object" },
            action_items_discussed: { type: "array" },
            partner_meeting_participants: { type: "array" },
            employee_meeting_participants: { type: "array" }
          },
          required: ["meeting_structure"]
        },
        transcript_summary: {
          type: "object",
          properties: {
            executive_summary: { type: "string" },
            key_points: { type: "array", items: { type: "string" } },
            action_items: { type: "array" },
            next_meeting: { type: "string", format: "date-time" }
          }
        },
        action_deduplication: {
          type: "object",
          properties: {
            duplicate_groups: { type: "array" },
            standalone_actions: { type: "array", items: { type: "integer" } }
          },
          required: ["duplicate_groups", "standalone_actions"]
        },
        custom: {
          type: "object",
          properties: {},
          required: []
        }
      }
    };
  },
  mounted() {
    this.fetchSuggestionTypes();
  },
  methods: {
    async fetchSuggestionTypes() {
      try {
        const response = await axios.get('/api/llmsuggestiontypes/');
        this.suggestionTypes = response.data;
      } catch (error) {
        console.error('Error fetching suggestion types:', error);
      }
    },

    hasSchema(suggestionType) {
      return suggestionType.json_schema &&
             Object.keys(suggestionType.json_schema).length > 0;
    },

    hasDisplaySchema(suggestionType) {
      return suggestionType.display_schema &&
             Object.keys(suggestionType.display_schema).length > 0;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    },

    openCreateModal() {
      this.editingSuggestionType = null;
      this.editActiveTab = 'Basic Info';
      this.formData = {
        name: '',
        description: '',
        prompt: '',
        json_schema: '',
        display_schema: '',
        default_data_sources: [],
        enable_web_search: false
      };
      this.editSchemaErrors = { json: '', display: '' };
      this.showCreateModal = true;
    },

    editSuggestionType(suggestionType) {
      this.editingSuggestionType = suggestionType;
      this.editActiveTab = 'Basic Info';

      // Extract config and output_schema from json_schema
      let jsonSchemaText = '';
      let defaultDataSources = [];
      let enableWebSearch = false;

      if (suggestionType.json_schema) {
        const schema = suggestionType.json_schema;

        // Check if schema has the new structure with config and output_schema
        if (schema.config || schema.output_schema) {
          // New structure
          jsonSchemaText = JSON.stringify(schema.output_schema || {}, null, 2);
          defaultDataSources = schema.config?.default_data_sources || [];
          enableWebSearch = schema.config?.enable_web_search || false;
        } else {
          // Old structure - just the schema itself
          jsonSchemaText = JSON.stringify(schema, null, 2);
        }
      }

      this.formData = {
        name: suggestionType.name,
        description: suggestionType.description || '',
        prompt: suggestionType.prompt || '',
        json_schema: jsonSchemaText,
        display_schema: suggestionType.display_schema
          ? JSON.stringify(suggestionType.display_schema, null, 2)
          : '',
        default_data_sources: defaultDataSources,
        enable_web_search: enableWebSearch
      };
      this.editSchemaErrors = { json: '', display: '' };
      this.showEditModal = true;
    },

    async toggleActive(suggestionType) {
      try {
        await axios.patch(`/api/llmsuggestiontypes/${suggestionType.id}/`, {
          is_active: !suggestionType.is_active
        });
        await this.fetchSuggestionTypes();
      } catch (error) {
        console.error('Error toggling status:', error);
        alert('Failed to update status');
      }
    },

    async saveSuggestionType() {
      if (!this.formData.name.trim()) {
        alert('Name is required');
        return;
      }

      try {
        const data = {
          name: this.formData.name,
          description: this.formData.description,
          prompt: this.formData.prompt || null
        };

        // Build json_schema with config and output_schema structure
        if (this.formData.json_schema && this.formData.json_schema.trim()) {
          try {
            const outputSchema = JSON.parse(this.formData.json_schema);

            // Combine config and output_schema into single json_schema
            data.json_schema = {
              config: {
                default_data_sources: this.formData.default_data_sources || [],
                enable_web_search: this.formData.enable_web_search || false
              },
              output_schema: outputSchema
            };
          } catch (e) {
            alert('Invalid JSON Schema format');
            return;
          }
        } else {
          // Just save config even without output schema
          data.json_schema = {
            config: {
              default_data_sources: this.formData.default_data_sources || [],
              enable_web_search: this.formData.enable_web_search || false
            }
          };
        }

        // Parse and add display schema if provided
        if (this.formData.display_schema && this.formData.display_schema.trim()) {
          try {
            data.display_schema = JSON.parse(this.formData.display_schema);
          } catch (e) {
            alert('Invalid Display Schema format');
            return;
          }
        }

        if (this.showEditModal) {
          await axios.patch(`/api/llmsuggestiontypes/${this.editingSuggestionType.id}/`, data);
        } else {
          await axios.post('/api/llmsuggestiontypes/', data);
        }

        await this.fetchSuggestionTypes();
        this.closeModals();
        alert('Suggestion type saved successfully');
      } catch (error) {
        console.error('Error saving suggestion type:', error);
        alert('Failed to save suggestion type');
      }
    },

    // Edit Modal Schema Validation Methods
    validateEditSchema(type) {
      const schemaText = type === 'json' ? this.formData.json_schema : this.formData.display_schema;

      if (!schemaText.trim()) {
        this.editSchemaErrors[type] = '';
        return;
      }

      try {
        const parsed = JSON.parse(schemaText);

        if (typeof parsed !== 'object' || parsed === null) {
          throw new Error('Schema must be a JSON object');
        }

        if (type === 'json' && !parsed.type) {
          throw new Error('JSON Schema must have a "type" property');
        }

        this.editSchemaErrors[type] = '';
      } catch (e) {
        this.editSchemaErrors[type] = e.message;
      }
    },

    formatEditSchema(type) {
      const schemaText = type === 'json' ? this.formData.json_schema : this.formData.display_schema;

      if (!schemaText.trim()) return;

      try {
        const parsed = JSON.parse(schemaText);
        const formatted = JSON.stringify(parsed, null, 2);

        if (type === 'json') {
          this.formData.json_schema = formatted;
        } else {
          this.formData.display_schema = formatted;
        }
      } catch (e) {
        alert('Invalid JSON format');
      }
    },

    onEditSchemaChange(type) {
      this.validateEditSchema(type);
    },

    selectAllEditDataSources() {
      this.formData.default_data_sources = this.availableDataSources.map(s => s.key);
    },

    deselectAllEditDataSources() {
      this.formData.default_data_sources = [];
    },


    // Generate Modal Methods
    addRequirement() {
      if (this.generateFormData.requirementInput.trim()) {
        this.generateFormData.requirements.push(this.generateFormData.requirementInput.trim());
        this.generateFormData.requirementInput = '';
      }
    },

    selectAllDataSources() {
      this.generateFormData.data_sources = this.availableDataSources.map(s => s.key);
    },

    deselectAllDataSources() {
      this.generateFormData.data_sources = [];
    },

    selectDefaultDataSources() {
      this.generateFormData.data_sources = ['entity_metadata', 'engagement_history', 'survey_responses'];
    },

    async generateSchemas() {
      if (!this.generateFormData.assessment_description.trim()) {
        alert('Assessment description is required');
        return;
      }

      if (!this.generateFormData.data_sources || this.generateFormData.data_sources.length === 0) {
        alert('Please select at least one data source');
        return;
      }

      this.isGenerating = true;
      this.generatedSchemas = null;

      try {
        const payload = {
          assessment_description: this.generateFormData.assessment_description,
          requirements: this.generateFormData.requirements,
          examples: this.generateFormData.examples,
          data_sources: this.generateFormData.data_sources
        };

        if (this.generateFormData.entity_id) {
          payload.entity_id = this.generateFormData.entity_id;
        }

        const response = await axios.post('/api/llmsuggestiontypes/generate-schema/', payload);

        if (response.data) {
          this.generatedSchemas = response.data;
        } else {
          throw new Error('No data received from generation');
        }
      } catch (error) {
        console.error('Error generating schemas:', error);
        alert(`Failed to generate schemas: ${error.response?.data?.error || error.message}`);
      } finally {
        this.isGenerating = false;
      }
    },

    // In the useGeneratedSchemas method:
    async useGeneratedSchemas() {
      if (!this.generatedSchemas) return;

      try {
        const data = {
          name: this.generatedSchemas.assessment_name,
          description: this.generatedSchemas.assessment_description,
          json_schema: this.generatedSchemas.assessment_json_schema,
          display_schema: this.generatedSchemas.display_schema,
          prompt: this.generatedSchemas.assessment_prompt  // Add this
        };

        await axios.post('/api/llmsuggestiontypes/', data);
        await this.fetchSuggestionTypes();
        this.closeGenerateModal();
        alert('New suggestion type created successfully');
      } catch (error) {
        console.error('Error creating suggestion type:', error);
        alert('Failed to create suggestion type');
      }
    },

    resetGeneration() {
      this.generatedSchemas = null;
      this.generateFormData.assessment_description = '';
      this.generateFormData.requirements = [];
      this.generateFormData.entity_id = null;
      this.generateFormData.data_sources = ['entity_metadata', 'engagement_history', 'survey_responses'];
    },

    closeModals() {
      this.showCreateModal = false;
      this.showEditModal = false;
      this.editingSuggestionType = null;
      this.editActiveTab = 'Basic Info';
      this.editSchemaErrors = { json: '', display: '' };
      this.formData = {
        name: '',
        description: '',
        prompt: '',
        json_schema: '',
        display_schema: '',
        default_data_sources: [],
        enable_web_search: false
      };
    },

    closeGenerateModal() {
      this.showGenerateModal = false;
      this.generatedSchemas = null;
      this.generateFormData = {
        assessment_description: '',
        requirements: [],
        requirementInput: '',
        examples: [],
        exampleInput: '',
        entity_id: null,
        data_sources: ['entity_metadata', 'engagement_history', 'survey_responses']
      };
      this.isGenerating = false;
    }
  }
};
</script>

<style scoped>
/* Add any custom styles here */
</style>
